import { useQuery } from '@tanstack/react-query';
import { ChevronRight, FolderTree } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchRoutes, useLocation, useParams } from 'react-router';

import { ROLE } from '@/common/role';
import type { SubAsset } from '@/common/sub-asset';
import { useAppContext } from '@/context/AppContext';
import { useDeletedPreview } from '@/hooks/use-deleted-preview';
import { usePermission } from '@/hooks/use-permission';
import { cn } from '@/lib/utils';
import { buildSubAssetPath, getTreeView } from '@/lib/utils/asset';
import { getAssetOptions } from '@/queries/assets/get-asset';
import { getListSubAssetsOptions } from '@/queries/sub-assets/list-sub-assets';

import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { SubAssetItem } from '../sub-asset-list/SubAssetItem';

export function SubAssetBreadcrumb() {
	const { t } = useTranslation();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const { assetId } = useParams<{ assetId: string }>();
	const location = useLocation();
	const { selectedAssetId, setSelectedAssetId } = useAppContext();
	const hasSubAssetsReadPermission = usePermission(ROLE.SUB_ASSETS_READ);
	const isNewAsset = assetId === 'new';
	// A deleted asset opened as a preview (GH #331): its sub-assets went with it, and only the deleted
	// asset endpoint returns it
	const isDeletedAsset = useDeletedPreview('asset');
	// Determine if we should show the selector
	const showAssetSelector = useMemo(() => {
		if (!hasSubAssetsReadPermission) {
			return false;
		}
		const assetMatchedRoutes = matchRoutes(
			[{ path: '/assets/:assetId' }, { path: '/assets/:assetId/inspections/*' }],
			location,
		);
		const isAssetRoute = !!assetMatchedRoutes?.length;
		// Show asset selector only if we are on an asset route but only in detail and insepction routes and not in sub-routes like finance, health, etc. also not for new asset
		return isAssetRoute && !isNewAsset && !isDeletedAsset;
	}, [location, isNewAsset, isDeletedAsset, hasSubAssetsReadPermission]);
	const { data: subAssetsResponse } = useQuery(
		getListSubAssetsOptions(assetId || '', { limit: 300, offset: 0 }, showAssetSelector),
	);
	const { data: assetDetail } = useQuery(getAssetOptions(assetId || '', !isNewAsset, { deleted: isDeletedAsset }));
	const assetWithSubAssets = useMemo((): SubAsset[] => {
		// If no assetId or asset detail, return empty array
		if (!assetId || !assetDetail?.id) return [];

		// Use getTreeView to structure sub-assets hierarchically
		const children = getTreeView(subAssetsResponse?.subAssets || []);
		return [
			{
				id: assetDetail.id,
				name: assetDetail.name,
				parentSubAssetId: null,
				children,
			},
		];
	}, [assetId, assetDetail?.id, assetDetail?.name, subAssetsResponse?.subAssets]);

	const breadcrumbPath = buildSubAssetPath(assetWithSubAssets, selectedAssetId || assetId || '');

	const handleSelect = (id: string) => {
		setIsDialogOpen(false);
		setSelectedAssetId(id);
	};

	// Ensure selectedAssetId is in sync with assetId from params
	useEffect(() => {
		if (assetId) {
			setSelectedAssetId(assetId);
		}
		// reset to assetId when route changes and not showing selector
		if (!showAssetSelector && assetId) {
			setSelectedAssetId(assetId);
		}
	}, [assetId, setSelectedAssetId, showAssetSelector]);

	return (
		<>
			<div className="flex items-center gap-2">
				{breadcrumbPath.map((item, index) => {
					const isLast = index === breadcrumbPath.length - 1;

					return (
						<div key={item.id} className="flex items-center gap-2">
							{index > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
							<button
								type="button"
								className={cn(
									'text-sm font-medium transition-colors rounded px-2 py-1 cursor-default hover:bg-transparent',
									isLast ? 'text-foreground' : 'text-muted-foreground/80',
								)}
								disabled={isLast}
							>
								{item.name}
							</button>
						</div>
					);
				})}

				{showAssetSelector && (
					<Button
						variant="ghost"
						size="sm"
						className="h-7 w-7 p-0 ml-1 hover:bg-muted text-muted-foreground hover:text-foreground"
						onClick={() => setIsDialogOpen(true)}
						title={t('assets.structure.changeAssetContext')}
					>
						<FolderTree className="h-4 w-4" />
					</Button>
				)}
			</div>

			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent className="bg-card border-border text-foreground max-w-2xl">
					<DialogHeader>
						<DialogTitle>{t('assets.structure.selectAsset')}</DialogTitle>
					</DialogHeader>
					<div className="py-4">
						<Card className="bg-muted/30 border-border overflow-hidden shadow-sm">
							<CardContent className="p-0">
								<div className="p-2 max-h-[60vh] overflow-y-auto">
									<SubAssetItem
										subAsset={assetWithSubAssets[0]}
										onSelect={handleSelect}
										selectedId={selectedAssetId || assetId || ''}
										viewOnlyMode
										defaultExpanded
									/>
								</div>
							</CardContent>
						</Card>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
